/**
 * @fileoverview Drop-in consent helpers.
 * @module helpers/consent
 *
 * @description
 * Two convenience wrappers over {@link ConsentManager} so you don't have to
 * wire the manager to the transport layer (or a DOM banner) by hand:
 *
 * - {@link setupConsent} — create a manager that automatically syncs the
 *   visitor's choices into FlowGrid's consent gates.
 * - {@link attachConsentBanner} — additionally wire an existing HTML banner's
 *   accept / reject buttons, with no framework required.
 *
 * ```ts
 * import { attachConsentBanner } from "flowgrid-sdk";
 *
 * // Markup: <div id="cookie-banner">… <button id="accept-btn"> <button id="reject-btn"> </div>
 * attachConsentBanner();
 * ```
 */

import { ConsentManager } from "../consent/manager";
import type {
  ConsentConfig,
  ConsentPreferences,
  CookieBannerConfig,
  CookieBannerTheme,
} from "../consent/types";
import { FlowGridTransport } from "../client/transport";

/**
 * Create a {@link ConsentManager} that keeps FlowGrid's transport-level consent
 * gates in sync automatically.
 *
 * On creation it pushes the visitor's stored preferences into the transport,
 * and it re-syncs on every change — so analytics/marketing events are gated
 * correctly without any extra wiring. Your own `onChange` (if provided) still
 * runs afterwards.
 *
 * @param config - Optional {@link ConsentConfig} overrides (cookie name, DNT, …).
 * @returns The configured {@link ConsentManager}.
 *
 * @example
 * ```ts
 * const consent = setupConsent({ cookieName: "my_app_consent", cookieDays: 180 });
 * if (!consent.hasConsented()) showMyBanner();
 * ```
 */
export function setupConsent(config?: ConsentConfig): ConsentManager {
  const userOnChange = config?.onChange;

  const manager = new ConsentManager({
    ...config,
    onChange: (prefs) => {
      syncTransport(prefs);
      userOnChange?.(prefs);
    },
  });

  // Push whatever is already stored so gates are correct before any interaction.
  syncTransport(manager.getPreferences());

  return manager;
}

function syncTransport(prefs: ConsentPreferences): void {
  FlowGridTransport.setConsent({
    analytics: prefs.analytics,
    marketing: prefs.marketing,
  });
}

/** Options for {@link attachConsentBanner}. */
export interface ConsentBannerOptions extends ConsentConfig {
  /** Element id of the banner container. Defaults to `"cookie-banner"`. */
  bannerId?: string;
  /**
   * The banner container element directly, bypassing `bannerId` lookup. Used by
   * the declarative no-code layer (Tier 3 BYO-markup) to wire a specific
   * `[data-cookie-ccbanner]` element.
   */
  container?: HTMLElement;
  /** Element id (within the banner) of the accept-all button. Defaults to `"accept-btn"`. */
  acceptId?: string;
  /** Element id (within the banner) of the reject-non-essential button. Defaults to `"reject-btn"`. */
  rejectId?: string;
  /**
   * Show the banner even if consent was already given (e.g. while debugging or
   * for a forced re-prompt). Defaults to `false`.
   */
  forceShow?: boolean;
  /**
   * Reuse an existing manager instead of creating one. Handy when you've
   * already called {@link setupConsent} elsewhere.
   */
  manager?: ConsentManager;
}

/** Handle returned by {@link attachConsentBanner}. */
export interface ConsentBannerHandle {
  /** The underlying consent manager. */
  manager: ConsentManager;
  /** Show the banner element. */
  show(): void;
  /** Hide the banner element. */
  hide(): void;
  /** Detach event listeners. Call on teardown (e.g. React effect cleanup). */
  destroy(): void;
}

/**
 * Wire an existing HTML cookie banner to a {@link ConsentManager}: clicking the
 * accept button calls `acceptAll()`, the reject button calls
 * `rejectNonEssential()`, and the banner hides itself afterwards. The banner is
 * shown automatically only if the visitor hasn't consented yet.
 *
 * Framework-agnostic and SSR-safe: returns `null` when there's no `document`
 * (server render) or no banner element is found.
 *
 * Expected markup (ids are configurable):
 * ```html
 * <div id="cookie-banner" style="display:none">
 *   <button id="reject-btn">Reject non-essential</button>
 *   <button id="accept-btn">Accept all</button>
 * </div>
 * ```
 *
 * @param options - Banner element ids plus any {@link ConsentConfig} overrides.
 * @returns A {@link ConsentBannerHandle}, or `null` when unavailable.
 */
export function attachConsentBanner(options?: ConsentBannerOptions): ConsentBannerHandle | null {
  if (typeof document === "undefined") return null;

  const {
    bannerId = "cookie-banner",
    container,
    acceptId = "accept-btn",
    rejectId = "reject-btn",
    forceShow = false,
    manager: providedManager,
    ...consentConfig
  } = options ?? {};

  const banner = container ?? document.getElementById(bannerId);
  if (!banner) return null;

  const manager = providedManager ?? setupConsent(consentConfig);

  const show = () => {
    banner.style.display = "";
  };
  const hide = () => {
    banner.style.display = "none";
  };

  // Resolve controls by data-role first (no-code BYO-markup contract), falling
  // back to the legacy element ids so existing integrations keep working.
  const acceptBtn =
    banner.querySelector<HTMLElement>("[data-cc-accept]") ?? banner.querySelector<HTMLElement>(`#${acceptId}`);
  const rejectBtn =
    banner.querySelector<HTMLElement>("[data-cc-reject]") ?? banner.querySelector<HTMLElement>(`#${rejectId}`);
  const customizeBtn = banner.querySelector<HTMLElement>("[data-cc-customize]");
  const saveBtn = banner.querySelector<HTMLElement>("[data-cc-save]");
  const toggleEls = Array.from(banner.querySelectorAll<HTMLInputElement>("[data-cc-toggle]"));

  const reflectToggles = () => {
    const prefs = manager.getPreferences();
    for (const el of toggleEls) {
      const cat = el.getAttribute("data-cc-toggle");
      if (cat === "analytics" || cat === "marketing" || cat === "preferences") {
        el.checked = !!prefs[cat];
      }
    }
  };

  const onAccept = () => {
    manager.acceptAll();
    hide();
  };
  const onReject = () => {
    manager.rejectNonEssential();
    hide();
  };
  const onSave = () => {
    const updates: Partial<Record<"analytics" | "marketing" | "preferences", boolean>> = {};
    for (const el of toggleEls) {
      const cat = el.getAttribute("data-cc-toggle");
      if (cat === "analytics" || cat === "marketing" || cat === "preferences") {
        updates[cat] = el.checked;
      }
    }
    manager.update(updates);
    hide();
  };
  // Reveal save button + per-category toggles when the visitor opts to customise.
  const onCustomize = () => {
    saveBtn?.removeAttribute("hidden");
    toggleEls.forEach((el) => el.closest<HTMLElement>("[data-cc-row]")?.removeAttribute("hidden"));
  };

  acceptBtn?.addEventListener("click", onAccept);
  rejectBtn?.addEventListener("click", onReject);
  saveBtn?.addEventListener("click", onSave);
  customizeBtn?.addEventListener("click", onCustomize);

  reflectToggles();

  // Only prompt visitors who haven't made a choice yet (unless forced open).
  if (manager.hasConsented() && !forceShow) hide();
  else show();

  return {
    manager,
    show,
    hide,
    destroy() {
      acceptBtn?.removeEventListener("click", onAccept);
      rejectBtn?.removeEventListener("click", onReject);
      saveBtn?.removeEventListener("click", onSave);
      customizeBtn?.removeEventListener("click", onCustomize);
    },
  };
}

/** Options for {@link renderConsentBanner}: consent + appearance, all optional. */
export interface RenderConsentBannerOptions extends ConsentConfig, CookieBannerConfig {
  /** Reuse an existing manager instead of creating one (e.g. from {@link setupConsent}). */
  manager?: ConsentManager;
}

// Resolved theme defaults — used when the caller omits `theme` fields.
const DEFAULT_BANNER_THEME: Required<CookieBannerTheme> = {
  position: "bottom",
  primaryColor: "#4F46E5",
  backgroundColor: "#FFFFFF",
  textColor: "#1F2937",
  borderRadius: "12px",
  fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  zIndex: 2147483000,
};

/**
 * Render a fully self-contained, configurable consent banner — **no markup
 * required**. FlowGrid injects the banner into the DOM, wires the buttons, and
 * keeps the transport-level consent gates in sync via {@link setupConsent}.
 *
 * Because it builds on {@link setupConsent} (which defaults
 * `requireExplicitConsent: false`), it is **opt-out by default**: analytics &
 * marketing events — including session-replay chunks — keep flowing unless the
 * visitor declines. Pass `mode: 'opt-in'` (or `requireExplicitConsent: true`)
 * to block non-essential tracking until the visitor clicks *Accept*. Necessary
 * cookies always run.
 *
 * SSR-safe: returns `null` when there is no `document`.
 *
 * @param options - {@link ConsentConfig} (cookie name, DNT, …) merged with
 *   {@link CookieBannerConfig} (copy, labels, privacy link, theme).
 * @returns A {@link ConsentBannerHandle} (`{ manager, show, hide, destroy }`),
 *   or `null` during SSR. Keep it to power a "Manage cookies" link via `show()`.
 *
 * @example
 * ```ts
 * import { renderConsentBanner } from "flowgrid-sdk";
 *
 * const banner = renderConsentBanner({
 *   title: "We value your privacy",
 *   description: "We use cookies to analyse traffic and improve your experience.",
 *   privacyPolicyUrl: "/privacy",
 *   theme: { position: "bottom-right", primaryColor: "#EBF212" },
 * });
 *
 * // Footer "Manage cookies" link:
 * document.getElementById("manage-cookies")?.addEventListener("click", () => banner?.show());
 * ```
 */
export function renderConsentBanner(options?: RenderConsentBannerOptions): ConsentBannerHandle | null {
  if (typeof document === "undefined") return null;

  const {
    // CookieBannerConfig
    mode,
    categories,
    unstyled = false,
    manageSelector,
    title = "Cookie preferences",
    description = "We use cookies to analyse traffic, remember your preferences, and improve your experience. You can accept all, reject non-essential, or choose which categories to allow.",
    acceptAllLabel = "Accept all",
    rejectAllLabel = "Reject non-essential",
    customizeLabel = "Customize",
    saveLabel = "Save preferences",
    privacyPolicyUrl,
    theme: themeOverrides,
    categoryDescriptions,
    forceShow = false,
    // pull manager out of the rest
    manager: providedManager,
    ...consentConfig
  } = options ?? {};

  const theme: Required<CookieBannerTheme> = { ...DEFAULT_BANNER_THEME, ...themeOverrides };

  // `mode` is the no-code-friendly alias for `requireExplicitConsent`: opt-out
  // (default) lets non-essential tracking run until decline; opt-in blocks it
  // until accept. An explicit `requireExplicitConsent` still wins.
  if (mode && consentConfig.requireExplicitConsent === undefined) {
    consentConfig.requireExplicitConsent = mode !== "opt-out";
  }

  const manager = providedManager ?? setupConsent(consentConfig);

  // Visible non-essential toggles. `necessary` is always shown locked-on and is
  // never part of this list.
  const visibleCategories: Array<"analytics" | "marketing"> =
    (categories ?? ["analytics", "marketing"]).filter(
      (c): c is "analytics" | "marketing" => c === "analytics" || c === "marketing",
    );

  // Skip per-element appearance styling in `unstyled` mode (Tier 2 BYO-CSS); the
  // stable `.fg-cc-*` classes and CSS variables are always emitted so the page's
  // own stylesheet can take over. Layout-critical display toggles stay in JS.
  const styled = (el: HTMLElement, css: string) => {
    if (!unstyled) el.style.cssText = css;
  };

  // Reuse an existing banner if renderConsentBanner ran twice (e.g. SPA remount).
  const CONTAINER_ID = "fg-consent-banner";
  document.getElementById(CONTAINER_ID)?.remove();

  // ── Layout helpers ──────────────────────────────────────────────────
  const positionStyle: Record<NonNullable<CookieBannerTheme["position"]>, string> = {
    top: "top:0;left:0;right:0;",
    bottom: "bottom:0;left:0;right:0;",
    "bottom-left": "bottom:16px;left:16px;max-width:420px;",
    "bottom-right": "bottom:16px;right:16px;max-width:420px;",
    center: "top:50%;left:50%;transform:translate(-50%,-50%);max-width:480px;",
  };
  const isLight = (() => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i.exec(theme.backgroundColor);
    if (!m) return true;
    const lum = (0.299 * parseInt(m[1], 16) + 0.587 * parseInt(m[2], 16) + 0.114 * parseInt(m[3], 16)) / 255;
    return lum > 0.5;
  })();
  const mutedText = isLight ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.7)";
  const subtleBg = isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)";
  const borderCol = isLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.14)";

  // ── Build DOM ───────────────────────────────────────────────────────
  const container = document.createElement("div");
  container.id = CONTAINER_ID;
  container.className = "fg-cc-banner";
  container.setAttribute("role", "dialog");
  container.setAttribute("aria-modal", "false");
  container.setAttribute("aria-label", "Cookie consent");
  // CSS variables are always emitted (even when unstyled) so a customer's own
  // stylesheet can read the configured palette via var(--fg-cc-*).
  container.style.setProperty("--fg-cc-bg", theme.backgroundColor);
  container.style.setProperty("--fg-cc-text", theme.textColor);
  container.style.setProperty("--fg-cc-primary", theme.primaryColor);
  container.style.setProperty("--fg-cc-radius", theme.borderRadius);
  container.style.setProperty("--fg-cc-z", String(theme.zIndex));
  styled(
    container,
    `position:fixed;${positionStyle[theme.position]}z-index:${theme.zIndex};` +
      `padding:16px;box-sizing:border-box;font-family:${theme.fontFamily};`,
  );

  const card = document.createElement("div");
  card.className = "fg-cc-card";
  styled(
    card,
    `background:${theme.backgroundColor};color:${theme.textColor};` +
      `border:1px solid ${borderCol};border-radius:${theme.borderRadius};` +
      `box-shadow:0 12px 40px rgba(0,0,0,0.25);padding:20px;max-width:520px;margin:0 auto;`,
  );
  container.appendChild(card);

  const h = document.createElement("p");
  h.className = "fg-cc-title";
  h.textContent = title;
  styled(h, "margin:0 0 6px;font-size:16px;font-weight:600;");
  card.appendChild(h);

  const desc = document.createElement("p");
  desc.className = "fg-cc-description";
  styled(desc, `margin:0 0 14px;font-size:13px;line-height:1.5;color:${mutedText};`);
  desc.textContent = description;
  if (privacyPolicyUrl) {
    const link = document.createElement("a");
    link.className = "fg-cc-link";
    link.href = privacyPolicyUrl;
    link.textContent = "Learn more";
    styled(link, `color:${theme.primaryColor};margin-left:4px;text-decoration:underline;`);
    desc.appendChild(document.createTextNode(" "));
    desc.appendChild(link);
  }
  card.appendChild(desc);

  // ── Customize panel (configurable category toggles) ─────────────────
  const panel = document.createElement("div");
  panel.className = "fg-cc-panel";
  styled(
    panel,
    `flex-direction:column;gap:10px;margin:0 0 14px;padding:12px;` +
      `background:${subtleBg};border-radius:8px;`,
  );
  panel.style.display = "none"; // structural — kept even when unstyled

  const prefs = manager.getPreferences();
  const toggles: Record<"analytics" | "marketing", HTMLInputElement> = {} as never;
  const labels: Record<"analytics" | "marketing", string> = {
    analytics: "Analytics",
    marketing: "Marketing",
  };
  const defaultDescs: Record<"analytics" | "marketing", string> = {
    analytics: "Page views, sessions, funnels, heatmaps, session replay.",
    marketing: "UTM tracking, attribution and retargeting.",
  };
  const rows: Array<[key: "necessary" | "analytics" | "marketing", label: string, desc: string, locked: boolean]> = [
    ["necessary", "Necessary", categoryDescriptions?.necessary ?? "Required for the site to function. Always on.", true],
    ...visibleCategories.map(
      (key) =>
        [key, labels[key], categoryDescriptions?.[key] ?? defaultDescs[key], false] as [
          "analytics" | "marketing",
          string,
          string,
          boolean,
        ],
    ),
  ];
  for (const [key, label, text, locked] of rows) {
    const row = document.createElement("label");
    row.className = "fg-cc-row";
    styled(row, "display:flex;align-items:flex-start;justify-content:space-between;gap:12px;cursor:pointer;");
    const labelWrap = document.createElement("div");
    const t = document.createElement("div");
    t.textContent = label;
    styled(t, "font-size:13px;font-weight:600;");
    const d = document.createElement("div");
    d.textContent = text;
    styled(d, `font-size:11px;color:${mutedText};margin-top:2px;`);
    labelWrap.appendChild(t);
    labelWrap.appendChild(d);
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.setAttribute("data-cc-toggle", key);
    styled(cb, "margin-top:2px;width:16px;height:16px;accent-color:" + theme.primaryColor + ";flex:0 0 auto;");
    cb.checked = locked ? true : (prefs[key] ?? false);
    cb.disabled = locked;
    if (!locked) toggles[key as "analytics" | "marketing"] = cb;
    row.appendChild(labelWrap);
    row.appendChild(cb);
    panel.appendChild(row);
  }
  card.appendChild(panel);

  // ── Buttons ─────────────────────────────────────────────────────────
  const actions = document.createElement("div");
  actions.className = "fg-cc-actions";
  styled(actions, "display:flex;flex-wrap:wrap;gap:8px;");
  card.appendChild(actions);

  const mkBtn = (label: string, modifier: string, primary: boolean): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `fg-cc-btn fg-cc-btn--${modifier}`;
    b.textContent = label;
    styled(
      b,
      `flex:1 1 auto;min-width:120px;padding:9px 14px;border-radius:8px;font-size:13px;` +
        `font-weight:600;cursor:pointer;border:1px solid ${primary ? theme.primaryColor : borderCol};` +
        (primary
          ? `background:${theme.primaryColor};color:${isLight ? "#fff" : "#000"};`
          : `background:transparent;color:${theme.textColor};`),
    );
    return b;
  };

  const customizeBtn = mkBtn(customizeLabel, "customize", false);
  const rejectBtn = mkBtn(rejectAllLabel, "reject", false);
  const saveBtn = mkBtn(saveLabel, "save", false);
  const acceptBtn = mkBtn(acceptAllLabel, "accept", true);
  saveBtn.style.display = "none"; // structural
  actions.appendChild(customizeBtn);
  actions.appendChild(rejectBtn);
  actions.appendChild(saveBtn);
  actions.appendChild(acceptBtn);

  // ── Accessibility: focus trap + restore, Esc = reject ───────────────
  let lastFocused: Element | null = null;
  const focusables = (): HTMLElement[] =>
    Array.from(
      container.querySelectorAll<HTMLElement>(
        'button:not([disabled]),a[href],input:not([disabled]),[tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onReject();
      return;
    }
    if (e.key !== "Tab") return;
    const items = focusables();
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // ── Behaviour ───────────────────────────────────────────────────────
  const show = () => {
    container.style.display = "";
    lastFocused = document.activeElement;
    container.addEventListener("keydown", onKeydown);
    // Move focus into the banner so keyboard users land on it.
    (focusables()[0] ?? container).focus?.();
  };
  const hide = () => {
    container.style.display = "none";
    container.removeEventListener("keydown", onKeydown);
    // Restore focus to whatever was focused before the banner opened.
    if (lastFocused && typeof (lastFocused as HTMLElement).focus === "function") {
      (lastFocused as HTMLElement).focus();
    }
    lastFocused = null;
  };

  function onReject() { manager.rejectNonEssential(); hide(); }
  const onAccept = () => { manager.acceptAll(); hide(); };
  const onSave = () => {
    manager.update({
      analytics: !!toggles.analytics?.checked,
      marketing: !!toggles.marketing?.checked,
    });
    hide();
  };
  const onCustomize = () => {
    const open = panel.style.display !== "none";
    panel.style.display = open ? "none" : "flex";
    saveBtn.style.display = open ? "none" : "";
  };

  acceptBtn.addEventListener("click", onAccept);
  rejectBtn.addEventListener("click", onReject);
  saveBtn.addEventListener("click", onSave);
  customizeBtn.addEventListener("click", onCustomize);

  document.body.appendChild(container);

  // ── "Manage cookies" re-open hook (footer link, etc.) ───────────────
  let manageEls: HTMLElement[] = [];
  const onManage = (e: Event) => { e.preventDefault(); handle.show(); };
  if (manageSelector) {
    manageEls = Array.from(document.querySelectorAll<HTMLElement>(manageSelector));
    manageEls.forEach((el) => el.addEventListener("click", onManage));
  }

  // Only prompt visitors who haven't decided yet (unless forced open).
  const shouldShow = !manager.hasConsented() || forceShow;
  if (shouldShow) show();
  else container.style.display = "none";

  const handle: ConsentBannerHandle = {
    manager,
    show,
    hide,
    destroy() {
      acceptBtn.removeEventListener("click", onAccept);
      rejectBtn.removeEventListener("click", onReject);
      saveBtn.removeEventListener("click", onSave);
      customizeBtn.removeEventListener("click", onCustomize);
      container.removeEventListener("keydown", onKeydown);
      manageEls.forEach((el) => el.removeEventListener("click", onManage));
      container.remove();
    },
  };
  return handle;
}
